import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Grid,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import api from '../../api/axios';
import {
  SNFFTagProfile,
  FTagDefinition,
  UNDERSTAFFING_CORE,
  UNDERSTAFFING_INDICATORS,
} from '../../types/inspections';

interface FTagProfileCardProps {
  ccn: string;
  schemeId?: string;
  compact?: boolean;
}

// Cache for F-Tag definitions to avoid refetching
let ftagDefinitionsCache: Record<string, FTagDefinition> | null = null;

const CATEGORY_LABELS: Record<string, string> = {
  staffing: 'Staffing',
  abuse_neglect: 'Abuse/Neglect',
  quality_of_care: 'Quality of Care',
  behavioral_health: 'Behavioral Health',
  pharmacy: 'Pharmacy',
  infection_control: 'Infection Control',
  dietary: 'Dietary',
};

const CATEGORY_COLORS: Record<string, string> = {
  staffing: '#1976d2',
  abuse_neglect: '#d32f2f',
  quality_of_care: '#ed6c02',
  behavioral_health: '#9c27b0',
  pharmacy: '#2e7d32',
  infection_control: '#0097a7',
  dietary: '#795548',
};

const FTagProfileCard: React.FC<FTagProfileCardProps> = ({ ccn, schemeId, compact = false }) => {
  const [profile, setProfile] = useState<SNFFTagProfile | null>(null);
  const [ftagDefinitions, setFtagDefinitions] = useState<Record<string, FTagDefinition>>(ftagDefinitionsCache || {});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFtagDefinitions = useCallback(async () => {
    // Use cache if available
    if (ftagDefinitionsCache) {
      setFtagDefinitions(ftagDefinitionsCache);
      return;
    }
    try {
      // Fetch all F-tag definitions (server returns all ~400 records without pagination)
      const response = await api.get('/schemes/ftags/');
      const definitions = response.data?.results || response.data || [];
      const definitionsMap: Record<string, FTagDefinition> = {};
      definitions.forEach((def: FTagDefinition) => {
        definitionsMap[def.ftag] = def;
      });
      ftagDefinitionsCache = definitionsMap;
      setFtagDefinitions(definitionsMap);
    } catch (err) {
      console.error('Error fetching F-tag definitions:', err);
    }
  }, []);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/schemes/snf-ftag-profiles/by-ccn/${ccn}/`, {
        params: { scheme: schemeId },
      });
      setProfile(response.data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setProfile(null);
      } else {
        setError('Failed to load F-tag profile');
        console.error('Error fetching F-tag profile:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [ccn, schemeId]);

  useEffect(() => {
    fetchFtagDefinitions();
  }, [fetchFtagDefinitions]);

  useEffect(() => {
    if (ccn) {
      fetchProfile();
    }
  }, [ccn, fetchProfile]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box py={2}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box py={2}>
        <Typography color="text.secondary">No F-tag profile available for this facility.</Typography>
      </Box>
    );
  }

  // Calculate top F-tags by count
  const topFtags = Object.entries(profile.ftag_counts)
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10);

  // Calculate category totals for bar chart
  const categoryData = Object.entries(profile.category_counts)
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a);

  const maxCategoryCount = Math.max(...Object.values(profile.category_counts), 1);

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} mb={2}>
        F-Tag Profile Summary
      </Typography>

      {/* Summary Stats Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Total Citations</Typography>
              <Typography variant="h5" fontWeight={600}>{profile.total_citations}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 3 }}>
          <Card variant="outlined" sx={{ height: '100%', borderColor: 'error.light' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="error.main">Harm/Jeopardy</Typography>
              <Typography variant="h5" fontWeight={600} color="error.main">
                {profile.total_harm_citations + profile.total_jeopardy_citations}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 3 }}>
          <Tooltip
            title={
              <Box sx={{ p: 1 }}>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  Core Understaffing F-Tags
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0, '& li': { mb: 0.5 } }}>
                  <li>F725: Sufficient Nursing Staff ({profile.ftag_counts?.F725 || 0})</li>
                  <li>F726: Competent Nursing Staff ({profile.ftag_counts?.F726 || 0})</li>
                  <li>F727: RN 8 Hours/7 Days ({profile.ftag_counts?.F727 || 0})</li>
                  <li>F851: Payroll-Based Journal ({profile.ftag_counts?.F851 || 0})</li>
                  <li>F686: Pressure Ulcers ({profile.ftag_counts?.F686 || 0})</li>
                  <li>F689: Accidents/Falls ({profile.ftag_counts?.F689 || 0})</li>
                </Box>
              </Box>
            }
            placement="bottom"
            arrow
          >
            <Card variant="outlined" sx={{ height: '100%', borderColor: 'warning.light', cursor: 'help' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="warning.main">Understaffing Core</Typography>
                <Typography variant="h5" fontWeight={600} color="warning.main">
                  {profile.understaffing_core_total}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  F725, F726, F727, F851, F686, F689
                </Typography>
              </CardContent>
            </Card>
          </Tooltip>
        </Grid>

        <Grid size={{ xs: 6, sm: 3 }}>
          <Tooltip
            title={
              <Box sx={{ p: 1 }}>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  Abuse Indicator Score Formula
                </Typography>
                <Typography variant="body2" mb={1}>
                  Weighted score combining staffing deficiencies and abuse citations:
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0, '& li': { mb: 0.5 } }}>
                  <li><strong>10×</strong> F600 harm citations (Abuse/Neglect)</li>
                  <li><strong>5×</strong> F725 (Sufficient Nursing Staff)</li>
                  <li><strong>5×</strong> F726 (Competent Nursing Staff)</li>
                  <li><strong>5×</strong> F727 (RN 8hrs/7days)</li>
                  <li><strong>3×</strong> F684 (Quality of Care)</li>
                  <li><strong>3×</strong> F686 (Treatment/Services to Prevent Pressure Ulcers)</li>
                  <li><strong>3×</strong> F689 (Free from Accident Hazards)</li>
                  <li><strong>3×</strong> F757 (Drug Regimen Review)</li>
                  <li><strong>2×</strong> F851 (Payroll-Based Journal)</li>
                </Box>
                <Typography variant="caption" sx={{ opacity: 0.8, mt: 1, display: 'block' }}>
                  Higher scores indicate greater risk of understaffing-related issues.
                </Typography>
              </Box>
            }
            placement="bottom"
            arrow
          >
            <Card variant="outlined" sx={{ height: '100%', borderColor: 'info.light', cursor: 'help' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="info.main">Abuse Indicator Score</Typography>
                <Typography variant="h5" fontWeight={600} color="info.main">
                  {profile.understaffing_abuse_indicator_score != null ? Number(profile.understaffing_abuse_indicator_score).toFixed(0) : '-'}
                </Typography>
                {profile.state_percentiles?.abuse_indicator != null && (
                  <Typography variant="caption" color="text.secondary">
                    {Number(profile.state_percentiles.abuse_indicator).toFixed(0)}th percentile in {profile.state}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Tooltip>
        </Grid>
      </Grid>

      {!compact && (
        <>
          {/* State Percentiles */}
          {profile.state_percentiles && Object.keys(profile.state_percentiles).length > 0 && (
            <Box mb={3}>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                State Percentiles ({profile.state})
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                {profile.state_percentiles.citations != null && (
                  <Tooltip title={`${Number(profile.state_percentiles.citations).toFixed(0)}th percentile for total citations in ${profile.state}`}>
                    <Chip
                      size="small"
                      label={`Citations: ${Number(profile.state_percentiles.citations).toFixed(0)}%`}
                      color={Number(profile.state_percentiles.citations) > 75 ? 'error' : Number(profile.state_percentiles.citations) > 50 ? 'warning' : 'success'}
                      variant="outlined"
                    />
                  </Tooltip>
                )}
                {profile.state_percentiles.harm != null && (
                  <Tooltip title={`${Number(profile.state_percentiles.harm).toFixed(0)}th percentile for harm citations in ${profile.state}`}>
                    <Chip
                      size="small"
                      label={`Harm: ${Number(profile.state_percentiles.harm).toFixed(0)}%`}
                      color={Number(profile.state_percentiles.harm) > 75 ? 'error' : Number(profile.state_percentiles.harm) > 50 ? 'warning' : 'success'}
                      variant="outlined"
                    />
                  </Tooltip>
                )}
                {profile.state_percentiles.staffing != null && (
                  <Tooltip title={`${Number(profile.state_percentiles.staffing).toFixed(0)}th percentile for staffing citations in ${profile.state}`}>
                    <Chip
                      size="small"
                      label={`Staffing: ${Number(profile.state_percentiles.staffing).toFixed(0)}%`}
                      color={Number(profile.state_percentiles.staffing) > 75 ? 'error' : Number(profile.state_percentiles.staffing) > 50 ? 'warning' : 'success'}
                      variant="outlined"
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
          )}

          <Grid container spacing={3}>
            {/* Category Breakdown */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                Citations by Category
              </Typography>
              <Box>
                {categoryData.map(([category, count]) => (
                  <Box key={category} mb={1}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Typography variant="body2">
                        {CATEGORY_LABELS[category] || category}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {count}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(count / maxCategoryCount) * 100}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: CATEGORY_COLORS[category] || 'primary.main',
                          borderRadius: 4,
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Top F-Tags */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                Top F-Tags
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {topFtags.map(([ftag, count]) => {
                  const isCore = UNDERSTAFFING_CORE.includes(ftag);
                  const isIndicator = UNDERSTAFFING_INDICATORS.includes(ftag);
                  const harmCount = profile.ftag_harm_counts[ftag] || 0;
                  const definition = ftagDefinitions[ftag];

                  return (
                    <Tooltip
                      key={ftag}
                      title={
                        <Box sx={{ maxWidth: 300 }}>
                          <Typography variant="body2" fontWeight={600}>{ftag}</Typography>
                          {definition?.description && (
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              {definition.description}
                            </Typography>
                          )}
                          <Typography variant="caption" component="div">
                            {count} total, {harmCount} harm
                            {isCore && ' (Core understaffing)'}
                            {isIndicator && ' (Understaffing indicator)'}
                          </Typography>
                        </Box>
                      }
                    >
                      <Chip
                        size="small"
                        label={`${ftag}: ${count}`}
                        color={isCore ? 'error' : isIndicator ? 'warning' : 'default'}
                        variant={isCore || isIndicator ? 'filled' : 'outlined'}
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: isCore || isIndicator ? 600 : 400,
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>

              {/* Severity Breakdown */}
              <Box mt={3}>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  Severity Distribution
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Tooltip title="No actual harm (A-C)">
                    <Chip
                      size="small"
                      label={`Minor: ${profile.minor_citations}`}
                      sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }}
                    />
                  </Tooltip>
                  <Tooltip title="Potential for harm (D-F)">
                    <Chip
                      size="small"
                      label={`Moderate: ${profile.moderate_citations}`}
                      sx={{ backgroundColor: '#fff3e0', color: '#ed6c02' }}
                    />
                  </Tooltip>
                  <Tooltip title="Actual harm (G-I)">
                    <Chip
                      size="small"
                      label={`Harm: ${profile.harm_citations}`}
                      sx={{ backgroundColor: '#ffebee', color: '#d32f2f' }}
                    />
                  </Tooltip>
                  <Tooltip title="Immediate jeopardy (J-L)">
                    <Chip
                      size="small"
                      label={`Jeopardy: ${profile.jeopardy_citations}`}
                      sx={{ backgroundColor: '#f3e5f5', color: '#7b1fa2' }}
                    />
                  </Tooltip>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default FTagProfileCard;